<template>
  <VFooter
    class="grey darken-3 p-0"
    height="274"
  >
    <slot>
      <VRow
          id="footer-content"
          class="grey darken-3 d-flex flex-column align-items-center"
          no-gutters
      >
        <VCol
            cols="12"
            class="position-relative d-flex justify-space-between p-0 w-100"
        >
        <slot name="customTopImages">
          <div class="position-absolute img-container flex-wrap">
            <div class="img-left">
              <slot name="imgLeft">
                <img
                  :src="require('@slg/web-customer-shared/src/assets/img/footer_img_left.svg')"
                  :alt="$t('imageAlt.footerDecorationLeft')"
                >
              </slot>
            </div>
            <div class="img-right">
              <slot name="imgRight">
                <img
                  :src="require('@slg/web-customer-shared/src/assets/img/footer_img_right.svg')"
                  :alt="$t('imageAlt.footerDecorationRight')"
                >
              </slot>
            </div>
          </div>
        </slot>
        </VCol>
        <div class="breakpoint-xl">
          <VCol
              cols="12"
              class="d-flex justify-center mt-6"
          >
          <!-- //NOSONAR --><a :href="mainFooterLogoLink"
            target="_blank"
            data-test="footerLogo"
          >
            <slot name="customLogo">
              <img
                  :src="require('@slg/web-customer-shared/src/assets/img/fleet-lab-logo.svg')"
                  :alt="$t('imageAlt.footerBrandLogo')"
              >
            </slot>
          </a>
          </VCol>
          <VCol
            v-if="isMobile"
            cols="12"
            class="d-flex justify-center pb-0"
          >
            <VBtn
              v-blur
              class="grey darken-3 box-shadow-elevation-0 border-radius-8"
              dark
              type="submit"
              @click="openFooterLinks"
            >
              <span class="button">{{ $t('footer.helpfulLinks') }} </span>
              <VIcon 
                class="ml-2"
                :class="isFooterOpened ? 'arrow-rotate' : ''"
              >
                {{ 'mdi-chevron-down' }}
              </VIcon>
            </VBtn>
          </VCol>
          <div ref="footer"> 
            <template v-if="isFooterFullyShown">
              <VCol
                  cols="12"
                  class="d-flex justify-center p-0 pr-0"
              >
                <FooterMenu
                  :menuItems="getMenuItemsWithStrapiArticles"
                  :isShownPrivacyPreferences="isShownPrivacyPreferences"
                  @openCookieDialog="$emit('openCookieDialog')"
                />
              </VCol>
              <VCol
                  cols="12"
                  class="d-flex justify-center pb-6"
              >
                <div>
                  <slot name="customSocial">
                    <a 
                      href="https://www.facebook.com/nightrider.lu"
                      class="pr-6"
                      target="_blank"
                    >
                      <img
                        class=""
                        :src="require('@slg/web-customer-shared/src/assets/img/Facebook.svg')"
                        :alt="$t('imageAlt.socialFacebook')"
                      >
                    </a>

                    <img
                        :src="require('@slg/web-customer-shared/src/assets/img/Instagram.svg')"
                        :alt="$t('imageAlt.socialInstagram')"
                    >
                  </slot>
                </div>
              </VCol>
              <VCol cols="12" class="d-flex justify-center pb-6 footer-additional align-start">
                  <slot name="bottomLeft">
                    <div>
                      <img
                        class="mr-6"
                        :src="require('@slg/web-customer-shared/src/assets/img/group.png')"
                        :alt="$t('imageAlt.footerPartnerLogos')"
                      >
                    </div>
                  </slot>
                  <slot name="bottomRight">
                    <div>
                      <div class="w-100"><a href="https://www.sales-lentz.lu" target="_blank">www.sales-lentz.lu</a><br></div>
                      <div class="w-100"><a href="https://www.slg.lu" target="_blank">www.slg.lu</a><br></div>
                      <div class="w-100"><a href="https://www.flibco.com" target="_blank">www.flibco.com</a><br></div>
                      <div class="w-100"><a href="https://www.executive-lane.com" target="_blank">www.executive-lane.com</a><br></div>
                    </div>
                  </slot>
              </VCol>
              </template>
          </div>
            <VCol
                cols="12"
                class="d-flex justify-center pb-6"
            >
              <div class="body-2 textWhite--text">
                <slot name="rights">
                  <span>{{ $t('general.copyright') }}</span>
                </slot>
              </div>
            </VCol>
        </div>
      </VRow>
    </slot>
  </VFooter>
</template>

<script lang="ts">
export default {
  name: 'SFooter',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import FooterMenu from './FooterMenu.vue';
import StrapiService from '@slg/web-customer-shared/src/services/StrapiService';
import { useCmsLocaleReload, useOpenLinkNewTab, useVuetify } from '../../composables';

const props = defineProps({
  menuItems: {
    type: Array,
    default: () => [],
  },
  isShownPrivacyPreferences: {
    type: Boolean,
    default: false,
  },
  mainFooterLogoLink: {
    type: String,
    default: 'https://fleetlab.com',
  },
});

const { setTargetAttributeToLink } = useOpenLinkNewTab();
const vuetify = useVuetify();

const isFooterOpened = ref(false);
const articlesList = ref<Array<{ showInTheFooter?: boolean; title?: string; slug?: string }>>([]);
const footer = ref<HTMLElement | null>(null);

function adaptArticles() {
  return articlesList.value.reduce((acc: Array<{ strapiText?: string; isAnotherDomain: boolean; path: { name: string; params: { name?: string } } }>, article) => {
    if (article.showInTheFooter) {
      acc.push({
        strapiText: article.title,
        isAnotherDomain: false,
        path: { name: 'Article', params: { name: article.slug } },
      });
    }
    return acc;
  }, []);
}

const getMenuItemsWithStrapiArticles = computed(() => (articlesList.value.length
  ? [...(props.menuItems as unknown[]), ...adaptArticles()]
  : props.menuItems));

const isMobile = computed(() => vuetify.breakpoint.smAndDown);
const isFooterFullyShown = computed(() => !isMobile.value || isFooterOpened.value);

async function loadCmsData() {
  try {
    articlesList.value = await StrapiService.getArticles();
  } catch (e) {
    articlesList.value = [];
  }
}

function openFooterLinks() {
  isFooterOpened.value = !isFooterOpened.value;
  if (isFooterOpened.value) {
    setTimeout(() => {
      (footer.value as HTMLElement).firstChild.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'start',
      });
    }, 100);
  }
}

watch(() => props.menuItems, () => {
  setTargetAttributeToLink(['footer-additional']);
}, { deep: true, immediate: true });

useCmsLocaleReload(loadCmsData);
</script>

<style scoped lang="scss">
@import "@slg/web-customer-shared/src/style/variables.scss";
.img-container {
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  z-index: 1;

  .img-left {
    position: absolute;
    max-width: 100%;
    left: 0;

    img {
      max-width: 100%;
    }
  }
  .img-right {
    position: absolute;
    max-width: 100%;
    right: 0;

    img {
      max-width: 100%;
    }
  }
}
.footer-additional {
  display: block;
  color: var(--v-textWhite-base) !important;
  font-size: $fs-16;

  & ::v-deep a {
    color: var(--v-textWhite-base) !important;
    text-decoration: underline;
  }
  & ::v-deep > div:first-child {
    display: flex;
    flex-wrap: wrap;
    width: 50%;
    justify-content: flex-end;
  }

  & ::v-deep > div:last-child {
    display: flex;
    flex-wrap: wrap;
    width: 50%;
    justify-content: flex-start;
    margin-left: 24px;
  }
  @media screen and (max-width: 480px) {
    flex-direction: column;
    align-items: center !important;
    & ::v-deep > div:first-child {
      justify-content: center;
      text-align: center;
      img {
        margin-right: 0 !important;
      }
    }

    & ::v-deep > div:last-child {
      margin-top: 24px;
      margin-left: 0;
      justify-content: center;
      text-align: center;
    }
  }
}
</style>
